// komplementaerGmbh.ts — die Komplementär-GmbH einer GmbH & Co. KG / UG & Co. KG
// erkennen, um sie weder anzulegen noch anzuzeigen (User 01.10.2026, T11).
//
// Die Komplementär-GmbH ist eine reine Verwaltungsgesellschaft: Sie führt die
// Geschäfte der KG und haftet, hat aber weder eigenes Vermögen noch eigenen
// Betrieb — bei ihr ist nichts zu versichern und nichts zu beraten.
//
// Erkannt wird sie an der GEPFLEGTEN ROLLE, nicht am Namen („… Verwaltungs
// GmbH" heißen auch Holdings, die ins Bild gehören):
//   • Rolleninhaber ist eine FIRMA — ein Mensch als Komplementär bleibt immer;
//   • Rolle „Komplementärin (Gesellschaft)" bzw. „Komplementär";
//   • die Gesellschaft, an der die Rolle gilt, hat das Profil KAPITAL_CO_KG
//     (GmbH & Co. KG, UG (haftungsbeschränkt) & Co. KG);
//   • Sicherungen: die Ausgangsfirma bleibt, und eine Firma mit eigenen
//     Strukturkanten (Tochter/Schwester/Beteiligung) ist zugleich Holding und
//     bleibt ebenfalls.
//
// Gegenstück im Backend: apps/api/src/firmendaten/komplementaerGmbh.ts (plus
// `rechtsformProfil` in registerRollen.ts). Die Seiten teilen keinen Code —
// Regeln hier und dort gleich halten. Bewusst ohne React, damit per Vitest prüfbar.

/** Profil-Schlüssel wie in Migration 202608150004. */
export const PROFIL_KAPITAL_CO_KG = "KAPITAL_CO_KG";
export const PROFIL_KG = "KG";

/** Katalog-Bezeichnung (Migration 202608150003) — wortgleich. */
export const BEZ_KOMPLEMENTAERIN_FIRMA = "Komplementärin (Gesellschaft)";

/** Hinweis im Register-Dialog. */
export const VERWALTUNGSGESELLSCHAFT_HINWEIS = "Verwaltungsgesellschaft – wird nicht angelegt";

const RECHTSFORMEN: ReadonlyArray<{ profil: string; werte: readonly string[] }> = [
  {
    profil: PROFIL_KAPITAL_CO_KG,
    werte: ["GmbH & Co. KG", "UG (haftungsbeschränkt) & Co. KG", "UG & Co. KG"],
  },
  { profil: PROFIL_KG, werte: ["Kommanditgesellschaft (KG)", "KG"] },
];

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

/**
 * Rechtsform → Profil-Schlüssel (Spiegel von `rechtsformProfil` im Backend).
 * Erkannt wird nur über Name und Kürzel, nie über Ids — PROD id 8 ist
 * „GmbH & Co. KG", DEV id 8 „AG".
 */
export function rechtsformProfil(
  legalForm?: { legalForm?: string | null; kuerzel?: string | null } | null,
): string | null {
  const name = norm(legalForm?.legalForm);
  const kuerzel = norm(legalForm?.kuerzel);
  if (!name && !kuerzel) return null;
  for (const eintrag of RECHTSFORMEN) {
    for (const wert of eintrag.werte) {
      const w = norm(wert);
      if (w && (w === name || w === kuerzel)) return eintrag.profil;
    }
  }
  return null;
}

/** „Komplementär", „Komplementärin (Gesellschaft)", „Ansprechpartner, Komplementär" … */
export function istKomplementaerFunktion(funktion?: string | null): boolean {
  return /komplement(ä|ae)r/i.test(funktion ?? "");
}

export type VerwaltungsgesellschaftsPruefung = {
  /** Profil der Gesellschaft, an der die Rolle gilt. */
  kgProfil?: string | null;
  /** Ist der Rolleninhaber eine Firma? */
  inhaberIstFirma: boolean;
  /** Rolle bzw. Funktion der Kante. */
  funktion?: string | null;
  /** Eigene Firmen-Strukturkanten des Rolleninhabers → bleibt (Holding). */
  inhaberHatStrukturkanten?: boolean;
  /** Der Rolleninhaber ist die gerade gezeigte Firma → bleibt immer. */
  inhaberIstAusgangsfirma?: boolean;
};

/** Die reine Erkennung. */
export function istVerwaltungsgesellschaft(p: VerwaltungsgesellschaftsPruefung): boolean {
  if (!p.inhaberIstFirma) return false;
  if (p.inhaberIstAusgangsfirma) return false;
  if (p.inhaberHatStrukturkanten) return false;
  if (!istKomplementaerFunktion(p.funktion)) return false;
  return p.kgProfil === PROFIL_KAPITAL_CO_KG;
}

/** Eintrag der Firmenkarte „Verantwortung fürs Unternehmen" (Form-State `rel_menschen`). */
type MenschEintrag = {
  ref: { contactId?: number; type: "Person" | "Firma" } | null;
  verant: {
    roles: Array<{ kind: string; verantwortlichkeit?: string; funktion?: string }>;
  } | null;
};

/**
 * Ist dieser Eintrag der Firmenkarte die Komplementär-GmbH der Firma?
 *
 * `strukturFirmenIds`: Firmen, mit denen die gezeigte Firma über eine
 * Strukturkante verbunden ist (Karte „Verbindungen zu Firmen"). Eine
 * Komplementärin, die dort zugleich als Mutter/Beteiligung steht, ist Holding
 * und bleibt sichtbar.
 */
export function istVerwaltungsgesellschaftsEintrag(
  e: MenschEintrag,
  kgProfil: string | null | undefined,
  opts: { firmaId?: number; strukturFirmenIds?: ReadonlySet<number> } = {},
): boolean {
  if (e.ref?.type !== "Firma") return false;
  const id = e.ref.contactId;
  return (e.verant?.roles ?? []).some((r) =>
    istVerwaltungsgesellschaft({
      kgProfil,
      inhaberIstFirma: true,
      funktion: r.kind === "mensch" ? r.verantwortlichkeit : r.kind === "gesellschafter" ? r.funktion : null,
      inhaberIstAusgangsfirma: id != null && id === opts.firmaId,
      inhaberHatStrukturkanten: id != null && !!opts.strukturFirmenIds?.has(id),
    }),
  );
}
